// React Imports
import React, { Fragment, useState, useEffect } from "react";

// MUI Imports
import {
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardHeader,
  MenuItem,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Backdrop,
  Popover,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';

// ** Icon Imports
import Icon from "../../components/icon";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// ** Custom Component Import
import CustomTextField from "../../components/mui/text-field";

// ** Layout Import
import UserLayout from "../../components/layouts/UserLayout";

// ** Dialog Imports
import CreateEstimateDialog from './component/CreateEstimate';
// import TagManagementDialog from "../Customers/component/TagManagement";
// import ConvertDialog from "./component/Convert";

import { useIndexLogic } from "./logic/index-logic";
// import AdvancedFilter from "../Requests/component/AdvancedFilter";
import { ToastContainer } from "react-toastify";
import TagManagementDialog from "screens/Customers/component/TagManagement";

const dropdownItemsStyle = {
  px: 6, 
  py: 2, 
  '&:hover': { 
    backgroundColor: 'action.hover', cursor: 'pointer' 
  }
}

// Custom Styled Button
const PopoverButton = ({
  handleOpenSchedule,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <Button
        variant="outlined"
        color="primary"
        endIcon={<KeyboardArrowDownIcon />}
        aria-describedby={id}
        onClick={handleClick}
      >
        Convert To
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ py: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6" sx={dropdownItemsStyle} onClick={handleOpenSchedule}>Estimate</Typography>
            <Typography variant="h6" sx={dropdownItemsStyle} onClick={handleOpenSchedule}>Work Order</Typography>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

const Estimates = () => {
  const { IndexProps } = useIndexLogic();

  const {
    columns,
    contentHeightFixed,
    handleOpenNewEstimateDlg,
    handleCloseNewEstimateDlg,
    schedulingReq,
    setSchedulingReq,
    openScheduleAst,
    setOpenScheduleAst,
    handleOpenScheduleAstDlg,
    handleCloseScheduleAstDlg,
    handleOpenTagManagementDlg,
    handleCloseTagManagementDlg,
    handleClearSelection,
    handleGridSelection,
    handleSearch,
    handleClearSearch,
    loading,
    setLoading,
    filteredRows,
    searchText,
    data,
    paginationText,
    paginationModel,
    setPaginationModel,
    openNewEstimateDlg,
    selectedRows,
    rowSelectionModel,
    setRowSelectionModel,
    openTagManagementDlg,
    tagManagementData,
    handleAddTag,
    handleDeleteTag,
    Tag,
    setTag,
    serviceRequest,
  } = IndexProps;

  return (
    <UserLayout
      contentHeightFixed={contentHeightFixed}
      activeLink={"Estimates"}
    >
      <Card>
        <CardHeader
          title={
            <Box
              sx={{
                mb: { xs: 3, sm: 0 },
                width: "100%",
              }}
            >
              <Box
                sx={{
                  border: "1px solid #D9D9D9",
                  borderRadius: 1,
                  px: 4,
                  py: 2,
                  maxWidth: "max-content",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Typography variant="body2" sx={{pointerEvents: 'none'}}>
                  {filteredRows.length === (data || []).length
                    ? `Displaying ${paginationText} records`
                    : `Displaying ${paginationText} records`}
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <CustomTextField
                value={searchText}
                placeholder="Search"
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="tabler:search" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchText && (
                        <IconButton
                          edge="end"
                          onClick={handleClearSearch}
                          aria-label="clear"
                          sx={{ mr: 2 }}
                        >
                          <Icon icon="tabler:x" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {/* <AdvancedFilter /> */}
              <Button
                onClick={handleOpenNewEstimateDlg}
                color="main"
                variant="contained"
                startIcon={<AddIcon />}
              >
                New Estimate
              </Button>
            </Box>
          }
          sx={{
            py: 4,
            flexDirection: ["column", "row"],
            "& .MuiCardHeader-action": { m: 0 },
            alignItems: ["flex-start", "center"],
          }}
        />
        {selectedRows.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              gap: 4,
              alignItems: "center",
              mx: 6,
              mt: 2,
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #E7E7E7",
                borderRadius: 1,
                pr: 4,
              }}
            >
              <IconButton sx={{ mx: 1 }} onClick={handleClearSelection}>
                <Icon icon="tabler:x" />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  cursor: "default",
                  userSelect: "none",
                }}
              >
                {selectedRows.length}{" "}
                {selectedRows.length === 1 ? "Record" : "Records"} Selected
              </Typography>
            </Box>
            <PopoverButton handleOpenSchedule={handleOpenScheduleAstDlg} schedulingReq={schedulingReq} setSchedulingReq={setSchedulingReq} />
            <CustomTextField select defaultValue="0">
              <MenuItem value="0" disabled>
                Status
              </MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </CustomTextField>
            <Button variant="contained" color="main">
              Apply Changes
            </Button>
            <Divider orientation="vertical" flexItem />
            <Box
              sx={{
                border: "1px solid #D9D9D9",
                borderRadius: 1,
                display: "flex",
                gap: 2,
                px: 2,
              }}
            >
              <Tooltip title="Tag Management">
                <IconButton onClick={handleOpenTagManagementDlg}>
                  <Icon icon="tabler:tag" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Selected">
                <IconButton color="error">
                  <Icon icon="tabler:trash" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
        <Box sx={{ height: '75vh' }}>
          <DataGridPro
            // pagination
            rows={filteredRows}
            rowHeight={62}
            columns={columns}
            checkboxSelection
            // pageSizeOptions={[10, 50, { value: filteredRows.length, label: "Show All" }]}
            disableRowSelectionOnClick
            // paginationModel={paginationModel}
            // onPaginationModelChange={setPaginationModel}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newSelectionModel) => {
              handleGridSelection(newSelectionModel);
              setRowSelectionModel(newSelectionModel);
            }}
            slots={{
              footer: () => null
            }}
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "none",
              },
            }}
          />
        </Box>
      </Card>

      {/* Pop up dialogs */}

      {/* New Estimate Dialog */}
      <CreateEstimateDialog
        open={openNewEstimateDlg}
        handleClose={handleCloseNewEstimateDlg}
        setLoading={setLoading}
      />

      {/* <TagManagementDialog
        open={openTagManagementDlg}
        handleClose={handleCloseTagManagementDlg}
        data={tagManagementData}
      /> */}

      <TagManagementDialog
        open = {openTagManagementDlg}
        handleClose = {handleCloseTagManagementDlg}      
        data = {{title:'Tag Management'}}
        handleAddTag = {handleAddTag}
        handleDeleteTag = {handleDeleteTag}
        tags = {serviceRequest?.tags}
        tag = {Tag}
        setTag = {setTag}
      />

      {/*<ToastContainer />*/}

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </UserLayout>
  );
};

export default Estimates;
